import { createSlice } from '@reduxjs/toolkit';
import { RESET_ALL_REDUCERS } from 'actions';

const INITIAL_STATE = {
    loading: false,
    error: null,
    source: null,
    workflow: {},
    company: {},
    ssn_config: {},
    mandatory_fields: [],
    verification_details: {},
    user_details: {},
    extended_checks: {},
    getTestResult: {},
};

const profileSlice = createSlice({
    name: 'profile',

    initialState: INITIAL_STATE,

    reducers: {
        setProfileData: (state, action) => {
            const { company, workflow, source } = action.payload ?? {};

            // A successful fetch clears any prior fetch error.
            state.error = null;

            // `source` (e.g. 'api' on the JWT-link flow) drives dummy-PII handling.
            if (source) {
                state.source = source;
            }

            if (company) {
                const { ssn_config, mandatory_fields, user } = company;
                const { verificationDetails, userDetails, extended_inputs } = user || {};

                state.company = { ...(company ?? {}) };
                state.ssn_config = ssn_config;
                state.mandatory_fields = mandatory_fields;
                state.user = { ...(user ?? {}) };
                state.verification_details = verificationDetails;
                state.user_details = userDetails;
                state.extended_checks = extended_inputs;
            }

            if (workflow) {
                state.workflow = workflow;
            }
        },

        // Shallow-merge placeholder company fields (pre-token branding shown on
        // the lander/onboarding screens) without disturbing a later full
        // setProfileData replacement.
        setCompanyDetails: (state, action) => {
            state.company = { ...state.company, ...(action.payload?.company ?? {}) };
        },

        setProfileLoading: (state, action) => {
            state.loading = action.payload;
        },

        setProfileError: (state, action) => {
            state.error = action.payload ?? null;
        },

        // Test-result data (`getTestResult`): loading flag on request, then the
        // fetched payload replaces it; an error payload clears loading too.
        setTestResultLoading: (state) => {
            state.getTestResult = { ...state.getTestResult, loading: true };
        },

        setTestResult: (state, action) => {
            state.getTestResult = { ...action.payload, loading: false };
        },

        /**
         * Puts the flag down without answering the question — for a request that stopped rather than
         * finished, which is what an aborted query is. `setTestResult` cannot do this job: it replaces
         * the payload, so using it here would throw away a result already on screen, and passing an
         * error would report a failure that never happened. Screens gate on `loading`, so something
         * has to clear it or they wait forever.
         */
        clearTestResultLoading: (state) => {
            state.getTestResult = { ...state.getTestResult, loading: false };
        },
    },

    extraReducers: (builder) => {
        // App-wide reset on session teardown (new token / re-auth).
        builder.addCase(RESET_ALL_REDUCERS, () => ({ ...INITIAL_STATE }));
    },
});

export const {
    setProfileData,
    setCompanyDetails,
    setProfileLoading,
    setProfileError,
    setTestResultLoading,
    setTestResult,
    clearTestResultLoading,
} = profileSlice.actions;

export default profileSlice.reducer;
